import IconButton from "@mui/material/IconButton";
import ModalWrapper from "../Shared/Modal";
import { CloseRounded, ContentCopyRounded } from "@mui/icons-material";
import Typography from "@mui/material/Typography";
import InputField from "../Shared/InputField";
import { useState } from "react";
import Button from "@mui/material/Button";
import LoadingButton from "../Shared/LoadingButton";
import { createAPIKey } from "../../redux/slices/apiKeysSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../redux/store";
import { showNotification } from "../../redux/slices/notificationSlice";

type ApiKeyModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

const ApiKeyModal = ({ open, setOpen }: ApiKeyModalProps) => {
  const [apiKeyDetail, setApiKeyDetail] = useState({ name: "", description: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [generatedApiKey, setGeneratedApiKey] = useState("");
  const dispatch = useDispatch<AppDispatch>();
  const { apiKeys } = useSelector((state: { apiKeys }) => state.apiKeys);

  const updateApiKeyField = (key: keyof typeof apiKeyDetail) => (val: string) => {
    setApiKeyDetail(prev => ({ ...prev, [key]: val }));
  };

  const handleGenerateAPIKey = async () => {
    if (!apiKeyDetail.name.trim()) {
      return;
    }
    if (apiKeys.length >= 3) {
      dispatch(showNotification({
        message: "You’ve reached the limit of 3 active API keys. Delete an active key to generate a new one.",
        severity: "warning",
        horizontal: "right",
      }));
      return;
    }
    setIsLoading(true);
    try {
      const result = await dispatch(createAPIKey(apiKeyDetail)).unwrap();
      setGeneratedApiKey(result.key);
      setShowApiKey(true);
      setApiKeyDetail({ name: "", description: "" });
    }
    catch (error) {
      console.error("Failed to create API key:", error);
      dispatch(showNotification({
        message: "Failed to create API key. Please try again.",
        severity: "error",
        horizontal: "right",
      }));
    }
    finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setOpen(false);
    setApiKeyDetail({ name: "", description: "" });
    setShowApiKey(false);
  };

  const handleCopyApiKey = async () => {
    if (generatedApiKey) {
      await navigator.clipboard.writeText(generatedApiKey);
      dispatch(showNotification({
        message: "Copied API key to clipboard.",
        severity: "success",
        horizontal: "right",
      }));
    }
  };

  return (
    <ModalWrapper
      open={open}
      handleClose={handleCancel}
      wrapperClassName="md:w-modal-lg"
      className="flex flex-col gap-y-8 p-6 bg-white dark:bg-background/paper-elevation-16 overflow-y-auto max-h-[90vh]"
    >
      {!showApiKey && (
        <>
          <div>
            <div className="flex items-center justify-between pb-2">
              <Typography variant="h6/medium-500" className="text-mui-black-87 dark:text-white">Generate a new API Key</Typography>
              <IconButton className="!h-8 !w-8" onClick={handleCancel}>
                <CloseRounded className=" text-mui-black-38 dark:text-mui-white-38" />
              </IconButton>
            </div>
            <Typography variant="body1/regular-400" className="text-mui-black-87 dark:text-mui-white-70">Make your first API call to TradeGPT</Typography>
          </div>
          <form className="flex flex-col gap-y-4 w-full">
            <InputField value={apiKeyDetail.name} setter={updateApiKeyField("name")} label="Name" showLabel={true} placeholder="Name the key" />
            <InputField value={apiKeyDetail.description} setter={updateApiKeyField("description")} label="Description" showLabel={true} placeholder="A description to know where the API key may be used (Optional)" />
          </form>
          <div className="flex flex-col gap-y-4 md:flex-row gap-x-6 justify-center">
            <Button variant="outlined" size="large" fullWidth className="button-outlined" onClick={handleCancel}>Cancel</Button>
            <LoadingButton isDisabled={!apiKeyDetail.name.trim()} isLoading={isLoading} onClick={handleGenerateAPIKey} variant="contained" size="large" fullWidth className="!bg-tgpt-secondary-light !normal-case items-center gap-x-2">Generate API Key</LoadingButton>
          </div>
        </>
      )}
      {showApiKey && (
        <>
          <div className="flex items-center justify-between">
            <Typography variant="h6/medium-500" className="text-mui-black-87 dark:text-white">API key generated</Typography>
            <IconButton className="!h-8 !w-8" onClick={handleCancel}>
              <CloseRounded className=" text-mui-black-38 dark:text-mui-white-38" />
            </IconButton>
          </div>
          <div className="flex flex-col gap-y-4">
            <Typography variant="body1/regular-400" className="text-mui-black-87 dark:text-mui-white-70">Save this key somewhere safe and accessible. For security reasons, you will not be able to view it again. If you lose this key, you&apos;ll need to generate a new one.</Typography>
            <div className="flex items-center gap-x-2">
              <InputField
                value={generatedApiKey}
                setter={() => {}}
                readOnly={true}
                className="w-full"
                inputClassName="!font-mono !text-sm !cursor-default"
                endAdornment={<IconButton onClick={handleCopyApiKey}><ContentCopyRounded className="!w-5 !h-5 text-mui-black-60 dark:text-mui-white-70" /></IconButton>}
              />
            </div>
          </div>
          <Button
            variant="contained"
            size="large"
            fullWidth
            className="!bg-tgpt-secondary-main !normal-case"
            onClick={handleCancel}
          >
            Close
          </Button>
        </>
      )}
    </ModalWrapper>
  );
};

export default ApiKeyModal;
